import { PublicClient } from 'viem';

import { DEFAULT_CREDITS_ATTEMPTS } from 'modules/prize-box-page/prize-box-page.constants';
import { IRaffleState } from 'modules/prize-box-page/prize-box-page.types';

import {
  MovesEnumRaffle,
  RoomStatusEnum,
  RoundWinnerOutcomes,
  WinnerStatusEnum,
} from '../../src/shared/constants/shared-enums';
import { RoomInfo } from '../../src/shared/types/app-types';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { CurrencyType } from 'shared/web3';

export const MOCK_ROOM_ID = 1234;
export const PAGE_TITLE = 'Rock, Paper, Scissors';
export const MOCK_PLAYER_A_ADDRESS = '0x1234567890abcdef1234567890abcdef12345678' as `0x${string}`;
export const MOCK_PLAYER_B_ADDRESS = '0xdeadbeefdeadbeefdeadbeefdeadbeefdeadbeef' as `0x${string}`;
export const MOCK_NONE_ADDRESS = '0x0000000000000000000000000000000000000000' as `0x${string}`;
export const DEFAULT_MOCK_ADDRESS = MOCK_PLAYER_A_ADDRESS as `0x${string}`;
export const MOCK_DEADLINE = BigInt(Math.floor(Date.now() / 1000) + 600);
export const MOCKED_BONUS_POINTS_VALUE = 1234n;

export const MOCK_MEMORY_STORAGE = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};
export const MOCKED_PRIZE_CRATE_RAFFLE_ADAPTER_STATE = {
  points: DEFAULT_CREDITS_ATTEMPTS,
  topHands: {
    [MovesEnumRaffle.Rock]: 14,
    [MovesEnumRaffle.Paper]: 18,
    [MovesEnumRaffle.Scissors]: 21,
  },
  quotaResetAt: Date.now() + 3600_000, // 1 hour from now
  extraCredits: 0,
  // Pools & slots
  poolUsdt: 1000,
  thresholdCents: 200,
  currentSlotBalance: 50,
  spendLimit: 500,
  slotDurationSec: 3600,
  lastSlotEndTime: Date.now() + 1800_000,
  needsRestore: false,
  contractReadable: true,
  recoveryAmount: 100, // $1
  attempts: DEFAULT_CREDITS_ATTEMPTS,
  windowStart: Date.now(),
} as IRaffleState & { attempts: number; needsRestore: boolean; contractReadable: boolean };

export const MOCK_ROUND_WITH_MOVES = (
  lastMoves: { playerAMove: number; playerBMove: number } | null,
  amIPlayerA: boolean,
) => {
  if (!lastMoves) return [];

  return [
    {
      moveA: lastMoves.playerAMove,
      moveB: lastMoves.playerBMove,
      encrMoveA: '0x',
      encrMoveB: '0x',
      winner: amIPlayerA ? RoundWinnerOutcomes.PlayerA : RoundWinnerOutcomes.PlayerB,
    },
  ];
};

export const MOCK_ROOMS: RoomInfo[] = [
  {
    data: {
      roomId: BigInt(1),
      playerA: MOCK_PLAYER_B_ADDRESS,
      playerB: NULL_ADDRESS,
      wins: 0,
      token: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', // USDT address
      bet: BigInt(1e6),
    },
    pot: BigInt(1e6),
    roundCounter: 0,
    winsCounter: { winsPlayerA: 0, winsPlayerB: 0 },
    closer: 0,
    deadline: BigInt(Date.now() + 3600_000), // 1 hour from now
    status: RoomStatusEnum.ReadyForGame,
    winnerStatus: 0,
  },
  {
    data: {
      roomId: BigInt(2),
      playerA: MOCK_PLAYER_B_ADDRESS,
      playerB: NULL_ADDRESS,
      wins: 1,
      token: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      bet: BigInt(5e6), // 0.5 USDC in wei
    },
    pot: BigInt(5e6), // 1 USDC pot
    roundCounter: 3,
    winsCounter: { winsPlayerA: 1, winsPlayerB: 2 },
    closer: 1,
    deadline: BigInt(Date.now() - 1000), // already expired
    status: RoomStatusEnum.Closed,
    winnerStatus: 2,
  },
  {
    data: {
      roomId: BigInt(3),
      playerA: MOCK_PLAYER_B_ADDRESS,
      playerB: NULL_ADDRESS, // No player B
      wins: 0,
      token: '0x912ce59144191c1204e64559fe8253a0e49e6548',
      bet: BigInt(2e18), // 2 ARB
    },
    pot: BigInt(2e18),
    roundCounter: 0,
    winsCounter: { winsPlayerA: 0, winsPlayerB: 0 },
    closer: 0,
    deadline: BigInt(Date.now() + 3600_000), // 10 minutes from now
    status: RoomStatusEnum.Open,
    winnerStatus: 0,
  },
  {
    data: {
      roomId: BigInt(4),
      playerA: MOCK_PLAYER_A_ADDRESS,
      playerB: NULL_ADDRESS,
      // Assuming wins is the number of wins for playerA
      wins: 0,
      token: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', // USDT address
      bet: BigInt(1e6), // 1 USDT in wei (assuming 6 decimals)
    },
    pot: BigInt(1e6), // 1 USDT pot
    roundCounter: 0,
    winsCounter: { winsPlayerA: 0, winsPlayerB: 0 },
    closer: 0,
    deadline: BigInt(Date.now() + 3600_000), // 1 hour from now
    status: RoomStatusEnum.ReadyForGame,
    winnerStatus: 0,
  },
];

export const MOCK_ROOM_FOR_USE_ACTIVE_ROOM = {
  winsTo: BigInt(3),
  bet: 5,
  roomBalance: 10,
  roomStatus: RoomStatusEnum.Open,
  playerA: {
    address: MOCK_PLAYER_A_ADDRESS,
    wins: 0n,
  },
  playerB: {
    address: MOCK_NONE_ADDRESS,
    wins: 0n,
  },
  roundCounter: 1,
  winnerStatus: WinnerStatusEnum.None,
  closerRoom: {
    closer: 0,
    deadline: MOCK_DEADLINE,
  },
  amIPlayerA: true,
  someoneElseGame: false,
};

export const getMockRoomForContractCall = () => {
  return [
    {
      roomId: 1234n,
      playerA: MOCK_PLAYER_A_ADDRESS,
      playerB: NULL_ADDRESS,
      wins: 3,
      token: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      bet: 5n * 10n ** 6n, // 5 USDT, 6 decimals
    },
    10n * 10n ** 6n, // pot: 10 USDT
    1, // roundCounter
    { winsPlayerA: 0, winsPlayerB: 0 }, // winsCounter
    0, // closer (enum value)
    MOCK_DEADLINE,
    RoomStatusEnum.ReadyForGame,
    WinnerStatusEnum.None,
  ] as const;
};

export const mockReadRoomIdCounter = async () => 4;

export const mockReadActiveRoomCounter = async () =>
  MOCK_ROOMS.filter((r) => r.status === RoomStatusEnum.Open).length;

export const mockReadGetRoomsInfo = async (
  _: PublicClient,
  from: number,
  to: number,
  signal?: AbortSignal,
): Promise<RoomInfo[]> => {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  return MOCK_ROOMS.filter((room) => {
    const id = Number(room.data.roomId);
    return id > from && id <= to;
  });
};

export const MOCK_STREAM_CURRENCY = {
  chainId: 42161,
  address: '0x912ce59144191c1204e64559fe8253a0e49e6548',
  symbol: 'ARB',
  decimals: 18,
  bet: ['1', '5', '10'],
  minBet: 1,
} as const satisfies CurrencyType;
